import { useState } from 'react';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/components';
import { useGetDepositQuote } from '@xhype/card-app-sdk';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import ReactJson from 'react-json-view';
import { z } from 'zod';

import { LoaderSkeleton } from './LoaderSkeleton';

/* Schemas */
export const CardDepositQuoteFormSchema = z.object({
  amount: z
    .string()
    .min(1, { message: 'Amount is required' })
    .refine((value) => !isNaN(Number(value)), {
      message: 'Amount must be a valid number',
    })
    .refine((value) => Number(value) >= 20, {
      message: 'Amount must be greater than 20',
    }),
  cardId: z.string().refine((value) => !value || !isNaN(Number(value)), {
    message: 'Card Id must be a valid number',
  }),
});

/* Types */
export type CardDepositQuoteFormType = z.infer<
  typeof CardDepositQuoteFormSchema
>;

/* Components */
export const CardDepositQuote = () => {
  const [amount, setAmount] = useState<string>('0');
  const [cardId, setCardId] = useState<number>(0);
  const [couponCode] = useState<string | undefined>(undefined);

  /* Search Form */
  const searchForm = useForm<CardDepositQuoteFormType>({
    resolver: zodResolver(CardDepositQuoteFormSchema),
  });

  /* Handlers */
  const handleSearch = async (data: CardDepositQuoteFormType) => {
    setCardId(Number(data.cardId));
    setAmount(data.amount);
  };

  /* Render */
  return (
    <section className='w-2/3 flex flex-col items-center justify-center gap-4'>
      <Form {...searchForm}>
        <form
          onSubmit={searchForm.handleSubmit(handleSearch)}
          className='w-full flex flex-col items-center justify-center gap-4'
        >
          <section className='w-full flex flex-row items-center justify-center gap-4'>
            <FormField
              control={searchForm.control}
              name='amount'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>Amount</FormLabel>
                  <FormControl>
                    <Input placeholder='1' type='number' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={searchForm.control}
              name='cardId'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>Card Id</FormLabel>
                  <FormControl>
                    <Input placeholder='1' type='number' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </section>
          <Button type='submit' className='cursor-pointer w-full'>
            Get deposit quote
          </Button>
        </form>
      </Form>
      {Number(amount) > 0 && (
        <GetDepositQuote
          amount={amount}
          cardId={cardId}
          couponCode={couponCode}
        />
      )}
    </section>
  );
};

/*Internal components */
type GetDepositQuoteProps = {
  amount: string;
  cardId: number;
  couponCode?: string;
};

const GetDepositQuote = ({
  amount,
  cardId,
  couponCode,
}: GetDepositQuoteProps) => {
  const { isLoading, data: depositQuoteData } = useGetDepositQuote({
    amount,
    cardId,
    couponCode,
  });

  /* Render */
  return (
    <section className='w-full flex flex-col items-center justify-center'>
      <section className='w-full flex flex-col items-center justify-center gap-4'>
        {!!isLoading && <LoaderSkeleton />}
        {!isLoading && depositQuoteData && (
          <section className='w-full flex flex-col items-start justify-start gap-2'>
            <ReactJson collapsed src={depositQuoteData || {}} />
          </section>
        )}
      </section>
    </section>
  );
};
